import React, { useEffect, useState } from 'react';
import { collection, doc, updateDoc, deleteDoc, getFirestore, onSnapshot } from 'firebase/firestore';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Usuario, RolUsuario, Obra } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { ObraMultiSelector } from '../obras/ObraMultiSelector';
import { Timestamp } from 'firebase/firestore';

const UsuariosTable: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [editandoUsuario, setEditandoUsuario] = useState<Usuario | null>(null);
  const [obras, setObras] = useState<Obra[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const { usuario: usuarioActual } = useAuth();
  const db = getFirestore();

  // Cargar obras
  useEffect(() => {
    const unsubscribeObras = onSnapshot(
      collection(db, 'obras'),
      (snapshot) => {
        const obrasData = snapshot.docs.map(doc => ({
          ...(doc.data() as Obra),
          id: doc.id
        }));
        setObras(obrasData);
      },
      () => {
        setError('Error al cargar las obras');
      }
    );

    return () => unsubscribeObras();
  }, [db]);

  useEffect(() => {
    const unsubscribeUsuarios = onSnapshot(
      collection(db, 'users'), // Changed from 'usuarios' to 'users'
      (snapshot) => {
        const usuariosData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            uid: doc.id,
            email: data.email,
            displayName: data.displayName,
            photoUrl: data.photoUrl,
            role: data.role || 'LECTOR',
            obrasAsignadas: data.obrasAsignadas || [],
            puedeVerTodasLasObras: data.puedeVerTodasLasObras || false,
            ...data
          } as Usuario;
        });
        setUsuarios(usuariosData);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setError('Error al cargar los usuarios');
        setLoading(false);
      }
    );

    return () => unsubscribeUsuarios();
  }, [db]);

  const handleRolChange = async (uid: string, nuevoRol: RolUsuario, rolAnterior: RolUsuario) => {
    if (!usuarioActual || usuarioActual.role !== 'ADMIN') {
      setError('No tienes permisos para cambiar roles');
      return;
    }

    if (uid === usuarioActual.uid) {
      setError('No puedes cambiar tu propio rol');
      return;
    }

    setLoadingAction(uid);
    setError(null);

    try {
      const userRef = doc(db, 'users', uid);
      const updates: Partial<Usuario> = { role: nuevoRol };

      // Si el nuevo rol no es EDITOR, eliminar obras asignadas?
      // Actually, LECTOR might also need assigned works if they are restricted.
      // But usually ADMIN sees all.
      // Let's keep assignments unless we want to clear them.
      // If switching to ADMIN, maybe clear assignments or keep them (doesn't matter as they see all).

      if (nuevoRol === 'ADMIN') {
        updates.puedeVerTodasLasObras = true;
      }

      await updateDoc(userRef, updates);

      setFeedback({
        message: `Rol actualizado exitosamente a ${nuevoRol}`,
        type: 'success'
      });

      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error(error);
      setError('Error al actualizar rol del usuario');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleUpdateObrasAsignadas = async (obrasAsignadas: string[]) => {
    if (!editandoUsuario) {
      setError('No hay usuario seleccionado para editar');
      return;
    }

    if (usuarioActual?.role !== 'ADMIN') {
      setError('Solo los administradores pueden asignar obras');
      return;
    }

    setLoadingAction(editandoUsuario.uid);
    setError(null);

    try {
      const userRef = doc(db, 'users', editandoUsuario.uid);
      await updateDoc(userRef, { obrasAsignadas });

      setFeedback({
        message: `Obras actualizadas exitosamente para ${editandoUsuario.displayName || editandoUsuario.email}`,
        type: 'success'
      });

      setTimeout(() => setFeedback(null), 3000);
      setEditandoUsuario(null);
    } catch (error) {
      console.error(error);
      setError('Error al actualizar obras asignadas');
    } finally {
      setLoadingAction(null);
    }
  };

  const getRolColor = (rol: RolUsuario) => {
    switch (rol) {
      case 'ADMIN':
        return 'error';
      case 'EDITOR':
        return 'primary';
      case 'LECTOR':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleDeleteUser = async (user: Usuario) => {
    if (!usuarioActual || usuarioActual.role !== 'ADMIN') {
      setError('No tienes permisos para eliminar usuarios');
      return;
    }

    if (user.uid === usuarioActual.uid) {
      setError('No puedes eliminar tu propia cuenta');
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar al usuario ${user.displayName || user.email}?`)) {
      return;
    }

    setLoadingAction(user.uid);
    setError(null);

    try {
      await deleteDoc(doc(db, 'users', user.uid));

      setFeedback({
        message: `Usuario ${user.displayName || user.email} eliminado exitosamente`,
        type: 'success'
      });

      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error(error);
      setError('Error al eliminar el usuario');
    } finally {
      setLoadingAction(null);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    // If it's a Firestore Timestamp
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleString('es-AR');
    }
    // If it's a standard Date object
    if (date instanceof Date) {
      return date.toLocaleString('es-AR');
    }
    // If it's a date string
    return new Date(date).toLocaleString('es-AR');
  };

  const handleRolChangeWrapper = async (user: Usuario, event: SelectChangeEvent) => {
    try {
      await handleRolChange(user.uid, event.target.value as RolUsuario, user.role);
    } catch (err) {
      setError('Error al actualizar el rol del usuario');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {feedback && (
        <Alert
          severity={feedback.type}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Rol</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Registro</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Último Acceso</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email / Verificado</TableCell>
              {usuarioActual?.role === 'ADMIN' && (
                <>
                  <TableCell sx={{ fontWeight: 'bold' }}>Obras Asignadas</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map((user) => (
              <TableRow
                key={user.uid}
                sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <TableCell>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={user.photoUrl || undefined} alt={user.displayName || 'U'}>
                      {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Typography variant="body2">{user.displayName || 'Sin nombre'}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    color={getRolColor(user.role)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">
                    {formatDate(user.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">
                    {formatDate(user.lastLoginAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{user.email}</Typography>
                    {user.emailVerified ? (
                      <Tooltip title="Email Verificado">
                        <CheckCircleIcon color="success" fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Email No Verificado">
                        <CancelIcon color="disabled" fontSize="small" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                {usuarioActual?.role === 'ADMIN' && (
                  <>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={
                          user.obrasAsignadas && user.obrasAsignadas.length > 0
                            ? obras
                              .filter(obra => user.obrasAsignadas.includes(obra.id))
                              .map(obra => obra.nombre)
                              .join('\n')
                            : 'Sin obras asignadas'
                        }>
                          <Typography variant="body2">
                            {user.obrasAsignadas && user.obrasAsignadas.length > 0
                              ? obras
                                .filter(obra => user.obrasAsignadas.includes(obra.id))
                                .map(obra => obra.nombre)
                                .slice(0, 2)
                                .join(', ') +
                              (user.obrasAsignadas.length > 2
                                ? ` y ${user.obrasAsignadas.length - 2} más`
                                : '')
                              : 'Sin obras asignadas'}
                          </Typography>
                        </Tooltip>
                        <Tooltip title="Editar obras asignadas">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setEditandoUsuario(user)}
                              disabled={user.uid === usuarioActual.uid || loadingAction === user.uid}
                            >
                              {loadingAction === user.uid ? (
                                <CircularProgress size={20} />
                              ) : (
                                <EditIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Select
                          value={user.role}
                          onChange={(e) => handleRolChangeWrapper(user, e)}
                          disabled={user.uid === usuarioActual.uid || loadingAction === user.uid}
                          size="small"
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="ADMIN">ADMIN</MenuItem>
                          <MenuItem value="EDITOR">EDITOR</MenuItem>
                          <MenuItem value="LECTOR">LECTOR</MenuItem>
                        </Select>
                        <Tooltip title="Eliminar usuario">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUser(user)}
                              disabled={user.uid === usuarioActual.uid || loadingAction === user.uid}
                              color="error"
                            >
                              {loadingAction === user.uid ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {editandoUsuario && (
        <ObraMultiSelector
          open={!!editandoUsuario}
          onClose={() => setEditandoUsuario(null)}
          onConfirm={handleUpdateObrasAsignadas}
          obras={obras}
          obrasSeleccionadas={editandoUsuario.obrasAsignadas || []}
          titulo={`Asignar obras a ${editandoUsuario.displayName || editandoUsuario.email}`}
        />
      )}
    </>
  );
};

export default UsuariosTable;